import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { activeFilterCount, type CatalogFilters, type CatalogSort } from '@/entities/catalog';
import type { WorkspaceDocument } from '@/entities/document';
import { DocumentList } from '@/features/document-catalog';

import styles from './HomeDashboard.module.css';
import { SectionHead } from './SectionHead';
import { catalogLink, documentsMatching, HOME_OFFERED } from './home-filters';
import type { HomePlaces } from '@/entities/places';

const SHOWN = 12;

export function HomeDocuments({
  documents,
  places,
  query,
  filters,
  sort,
  userId,
  locale,
  full = false,
}: {
  documents: readonly WorkspaceDocument[];
  places: HomePlaces;
  query: string;
  filters: CatalogFilters;
  sort: CatalogSort;
  userId: string | null;
  locale: string;
  /** The complete catalog page: every match, no section header. */
  full?: boolean;
}) {
  const { t } = useTranslation();
  const matches = useMemo(
    () => documentsMatching(documents, places, filters, userId, query, sort, locale),
    [documents, places, filters, userId, query, sort, locale],
  );
  const locations = useMemo(
    () => new Map(matches.map((d) => [d.id, places.locationOf(d)])),
    [matches, places],
  );
  if (!full && filters.type !== 'all' && filters.type !== 'document') return null;
  // Never an empty block for someone without notes, unless notes were asked for explicitly.
  if (!full && documents.length === 0 && filters.type !== 'document') return null;
  const narrowed = query.trim() !== '' || activeFilterCount(filters, HOME_OFFERED) > 0;
  return (
    <section className={styles.databases} aria-labelledby="home-documents-title">
      {!full && (
        <SectionHead
          id="home-documents-title"
          title={t('documents.title')}
          count={matches.length}
          to={catalogLink('/app/browse/documents', query)}
          linkLabel={t('home.viewAllDocuments')}
          linkTestId="all-documents-link"
        />
      )}
      <DocumentList
        documents={full ? matches : matches.slice(0, SHOWN)}
        locations={locations}
        emptyText={narrowed ? t('documents.noMatches') : t('documents.empty')}
      />
      {!full && matches.length > SHOWN && (
        <p className={styles.moreNote}>
          {t('documents.moreInWorkspaces', { count: matches.length - SHOWN })}
        </p>
      )}
    </section>
  );
}
